import { useState, useEffect, useRef, useCallback } from 'react';
import { Template } from '../../../mocks/websiteTemplates';
import { semanticScore } from '../websiteBuilderConstants';

interface Props {
  templates: Template[];
  onClose: () => void;
  onPreview: (t: Template) => void;
}

type Step = 1 | 2 | 3 | 4;

const QUICK_PICKS = [
  { label: 'Portfolio', icon: 'ri-user-star-line', query: 'a creative portfolio to showcase my design work' },
  { label: 'Online Store', icon: 'ri-shopping-bag-3-line', query: 'an ecommerce store to sell products online' },
  { label: 'Restaurant', icon: 'ri-restaurant-line', query: 'a restaurant website with menu and reservations' },
  { label: 'SaaS / App', icon: 'ri-code-box-line', query: 'a SaaS product landing page with pricing and features' },
  { label: 'Fitness / Gym', icon: 'ri-heart-pulse-line', query: 'a fitness gym or wellness studio website' },
  { label: 'Real Estate', icon: 'ri-building-line', query: 'a real estate agency to list properties' },
  { label: 'Blog / Media', icon: 'ri-article-line', query: 'a blog or media publication website' },
  { label: 'Education', icon: 'ri-graduation-cap-line', query: 'an online learning or education platform' },
  { label: 'Agency', icon: 'ri-briefcase-line', query: 'a creative or digital marketing agency website' },
  { label: 'Music / Events', icon: 'ri-music-line', query: 'a music artist or events entertainment website' },
];

const STYLE_OPTIONS = [
  { id: 'Minimal', label: 'Minimal', gradient: 'from-gray-300 to-gray-500', desc: 'Clean & simple' },
  { id: 'Bold', label: 'Bold', gradient: 'from-orange-400 to-red-500', desc: 'Strong impact' },
  { id: 'Dark', label: 'Dark', gradient: 'from-gray-700 to-gray-900', desc: 'Sleek & moody' },
  { id: 'Colorful', label: 'Colorful', gradient: 'from-pink-400 to-fuchsia-500', desc: 'Vibrant & fun' },
  { id: 'Warm', label: 'Warm', gradient: 'from-amber-400 to-orange-500', desc: 'Cozy & inviting' },
  { id: 'Elegant', label: 'Elegant', gradient: 'from-rose-400 to-pink-600', desc: 'Refined luxury' },
  { id: 'Modern', label: 'Modern', gradient: 'from-violet-400 to-indigo-500', desc: 'Contemporary' },
  { id: 'Corporate', label: 'Corporate', gradient: 'from-slate-500 to-slate-700', desc: 'Professional' },
  { id: 'Creative', label: 'Creative', gradient: 'from-purple-400 to-fuchsia-500', desc: 'Artistic & unique' },
  { id: 'Clean', label: 'Clean', gradient: 'from-sky-400 to-cyan-500', desc: 'Fresh & airy' },
];

const STATUS_MESSAGES = [
  'Understanding your vision...',
  'Scanning 335 templates...',
  'Analyzing style preferences...',
  'Scoring template matches...',
  'Ranking top results...',
  'Almost done...',
];

const PLACEHOLDER_EXAMPLES = [
  'a dark portfolio for a photographer who shoots urban landscapes...',
  'a minimal SaaS landing page for a project management tool...',
  'a warm restaurant website for a family-owned Italian bistro...',
  'a bold fashion brand store with vibrant editorial photography...',
  'a clean wellness clinic website for a holistic health practitioner...',
];

function computeResults(templates: Template[], description: string, styles: string[]) {
  return templates
    .map((t) => {
      let score = description.trim() ? semanticScore(t, description) : 0;
      styles.forEach((s) => { if (t.tags.includes(s)) score += 3; });
      if (t.badge === 'Popular') score += 0.5;
      return { t, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
}

function normalizeScore(score: number, max: number): number {
  if (max === 0) return 0;
  return Math.min(100, Math.round((score / max) * 100));
}

export default function AIBuilderModal({ templates, onClose, onPreview }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [description, setDescription] = useState('');
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [statusIdx, setStatusIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<Array<{ t: Template; score: number }>>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Rotate placeholder
  useEffect(() => {
    if (step !== 1) return;
    const t = setInterval(() => setPlaceholderIdx((v) => (v + 1) % PLACEHOLDER_EXAMPLES.length), 3200);
    return () => clearInterval(t);
  }, [step]);

  // Generating animation
  useEffect(() => {
    if (step !== 3) return;
    setProgress(0);
    setStatusIdx(0);

    const start = Date.now();
    const duration = 2600;

    const msgTimer = setInterval(() => {
      setStatusIdx((v) => (v + 1) % STATUS_MESSAGES.length);
    }, 430);

    const raf = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, Math.round((elapsed / duration) * 100));
      setProgress(pct);
      if (elapsed < duration) requestAnimationFrame(raf);
      else {
        clearInterval(msgTimer);
        const r = computeResults(templates, description, selectedStyles);
        setResults(r);
        goToStep(4);
      }
    };
    requestAnimationFrame(raf);
    return () => clearInterval(msgTimer);
  }, [step]);

  const goToStep = useCallback((s: Step) => {
    setIsTransitioning(true);
    setTimeout(() => { setStep(s); setIsTransitioning(false); }, 180);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  const toggleStyle = (id: string) =>
    setSelectedStyles((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);

  const canProceedStep1 = description.trim().length > 6;
  const canProceedStep2 = selectedStyles.length > 0 || description.trim().length > 6;

  const maxScore = results.length > 0 ? results[0].score : 1;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col" style={{ background: '#07070c' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4 border-b flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' }}>
            <i className="ri-sparkling-2-fill text-white text-sm"></i>
          </div>
          <span className="text-sm font-bold text-white">OpenClaw AI Builder</span>
        </div>
        {/* Step indicator */}
        {step !== 3 && (
          <div className="flex items-center gap-2">
            {([1, 2, 4] as const).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step === s ? 'bg-violet-500 text-white' :
                  ((step === 2 && s === 1) || step === 4) ? 'bg-emerald-500 text-white' :
                  'bg-white/10 text-white/30'
                }`}>
                  {((step === 2 && s === 1) || step === 4) && s !== step
                    ? <i className="ri-check-line text-xs"></i>
                    : i + 1}
                </div>
                {i < 2 && <div className={`w-8 h-px ${step > s ? 'bg-emerald-500/60' : 'bg-white/10'}`}></div>}
              </div>
            ))}
          </div>
        )}
        <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all cursor-pointer">
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto flex flex-col transition-opacity duration-180"
        style={{ opacity: isTransitioning ? 0 : 1 }}
      >

        {/* ── STEP 1: Describe ── */}
        {step === 1 && (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 max-w-3xl mx-auto w-full">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6" style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)' }}>
                <i className="ri-sparkling-2-fill text-violet-400 text-xs"></i>
                <span className="text-xs font-semibold text-violet-400 uppercase tracking-widest">Step 1 of 3</span>
              </div>
              <h2 className="text-4xl font-black text-white mb-3 leading-tight">What are you building?</h2>
              <p className="text-white/50 text-base">Describe your vision — the more detail, the better the match.</p>
            </div>

            {/* Textarea */}
            <div className="w-full mb-6 rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(124,58,237,0.4)', background: 'rgba(255,255,255,0.03)' }}>
              <textarea
                ref={textareaRef}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={`e.g., ${PLACEHOLDER_EXAMPLES[placeholderIdx]}`}
                rows={4}
                className="w-full px-6 py-5 bg-transparent text-white placeholder-white/20 text-base resize-none focus:outline-none leading-relaxed"
                maxLength={500}
              />
              <div className="flex items-center justify-between px-6 pb-4">
                <span className="text-xs text-white/20">{description.length}/500</span>
                {description.length > 0 && (
                  <button onClick={() => setDescription('')} className="text-xs text-white/30 hover:text-white/60 cursor-pointer">Clear</button>
                )}
              </div>
            </div>

            {/* Quick picks */}
            <div className="w-full mb-8">
              <p className="text-xs text-white/30 font-semibold uppercase tracking-widest mb-3">Or pick a quick start</p>
              <div className="grid grid-cols-5 gap-2">
                {QUICK_PICKS.map((pick) => (
                  <button
                    key={pick.label}
                    onClick={() => setDescription(pick.query)}
                    className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl text-center transition-all cursor-pointer group"
                    style={{
                      background: description === pick.query ? 'rgba(124,58,237,0.25)' : 'rgba(255,255,255,0.04)',
                      border: description === pick.query ? '1px solid rgba(124,58,237,0.5)' : '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <i className={`${pick.icon} text-lg ${description === pick.query ? 'text-violet-400' : 'text-white/40 group-hover:text-white/60'}`}></i>
                    <span className={`text-xs font-medium whitespace-nowrap ${description === pick.query ? 'text-violet-300' : 'text-white/40 group-hover:text-white/60'}`}>{pick.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => canProceedStep1 && goToStep(2)}
              className={`flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-bold transition-all cursor-pointer whitespace-nowrap ${
                canProceedStep1
                  ? 'text-white hover:opacity-90'
                  : 'text-white/30 cursor-not-allowed'
              }`}
              style={canProceedStep1 ? { background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' } : { background: 'rgba(255,255,255,0.06)' }}
            >
              <span>Choose Your Style</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}

        {/* ── STEP 2: Style ── */}
        {step === 2 && (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 max-w-3xl mx-auto w-full">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6" style={{ background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.3)' }}>
                <i className="ri-palette-fill text-cyan-400 text-xs"></i>
                <span className="text-xs font-semibold text-cyan-400 uppercase tracking-widest">Step 2 of 3</span>
              </div>
              <h2 className="text-4xl font-black text-white mb-3 leading-tight">What&apos;s your style?</h2>
              <p className="text-white/50 text-base">Pick one or more — AI will weight these in your results.</p>
            </div>

            {/* Style tiles */}
            <div className="w-full grid grid-cols-5 gap-3 mb-8">
              {STYLE_OPTIONS.map((style) => {
                const active = selectedStyles.includes(style.id);
                return (
                  <button
                    key={style.id}
                    onClick={() => toggleStyle(style.id)}
                    className={`flex flex-col gap-3 p-4 rounded-2xl text-left transition-all cursor-pointer ${active ? 'scale-[1.03]' : 'hover:scale-[1.02]'}`}
                    style={{
                      border: active ? '2px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.07)',
                      background: active ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                    }}
                  >
                    <div className={`w-full h-8 rounded-xl bg-gradient-to-r ${style.gradient}`}></div>
                    <div>
                      <p className={`text-sm font-bold ${active ? 'text-white' : 'text-white/60'}`}>{style.label}</p>
                      <p className={`text-xs mt-0.5 ${active ? 'text-white/60' : 'text-white/30'}`}>{style.desc}</p>
                    </div>
                    {active && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-gray-900 text-xs"></i>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {selectedStyles.length > 0 && (
              <p className="text-xs text-white/30 mb-6">
                Selected: <span className="text-violet-400 font-medium">{selectedStyles.join(', ')}</span>
              </p>
            )}

            <div className="flex items-center gap-4">
              <button onClick={() => goToStep(1)} className="flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-medium text-white/50 hover:text-white transition-colors cursor-pointer whitespace-nowrap" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
              <button
                onClick={() => canProceedStep2 && goToStep(3)}
                className={`flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-bold transition-all cursor-pointer whitespace-nowrap ${canProceedStep2 ? 'text-white hover:opacity-90' : 'text-white/30 cursor-not-allowed'}`}
                style={canProceedStep2 ? { background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' } : { background: 'rgba(255,255,255,0.06)' }}
              >
                <i className="ri-sparkling-2-line"></i>
                Find My Templates
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Generating ── */}
        {step === 3 && (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-16">
            {/* Orbit rings */}
            <div className="relative mb-10 flex items-center justify-center" style={{ width: 160, height: 160 }}>
              <div className="absolute inset-0 rounded-full border border-violet-500/20 animate-spin" style={{ animationDuration: '8s' }}></div>
              <div className="absolute rounded-full border border-cyan-400/15 animate-spin" style={{ inset: 12, animationDuration: '5s', animationDirection: 'reverse' }}></div>
              <div className="absolute rounded-full border border-violet-400/10 animate-spin" style={{ inset: 24, animationDuration: '3.5s' }}></div>
              <div className="relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' }}>
                <i className="ri-sparkling-2-fill text-white text-2xl animate-pulse"></i>
              </div>
            </div>

            <h2 className="text-3xl font-black text-white mb-3 text-center">Analyzing templates...</h2>
            <p className="text-white/40 text-sm mb-8 text-center min-h-5 transition-all">{STATUS_MESSAGES[statusIdx]}</p>

            {/* Progress bar */}
            <div className="w-80 h-1.5 rounded-full overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div
                className="h-full rounded-full transition-all duration-100"
                style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#7c3aed,#06b6d4)' }}
              ></div>
            </div>
            <p className="text-white/20 text-xs">{progress}%</p>

            {description.trim() && (
              <div className="mt-10 max-w-sm text-center px-6 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white/30 text-xs italic">&ldquo;{description.length > 80 ? description.slice(0, 80) + '...' : description}&rdquo;</p>
                {selectedStyles.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1 mt-2">
                    {selectedStyles.map((s) => <span key={s} className="px-2 py-0.5 text-xs rounded-full text-violet-400" style={{ background: 'rgba(124,58,237,0.15)' }}>{s}</span>)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 4: Results ── */}
        {step === 4 && (
          <div className="flex-1 px-8 py-10 max-w-6xl mx-auto w-full">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <i className="ri-checkbox-circle-fill text-emerald-400 text-xs"></i>
                <span className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">
                  {results.length} templates matched
                </span>
              </div>
              <h2 className="text-3xl font-black text-white mb-2">
                {results.length > 0 ? 'Your perfect matches' : 'Try a different description'}
              </h2>
              {description.trim() && (
                <p className="text-white/40 text-sm max-w-lg mx-auto">
                  Based on: &ldquo;{description.length > 60 ? description.slice(0, 60) + '...' : description}&rdquo;
                  {selectedStyles.length > 0 && <span> + <span className="text-violet-400">{selectedStyles.join(', ')}</span></span>}
                </p>
              )}
            </div>

            {results.length === 0 ? (
              <div className="text-center py-16">
                <i className="ri-search-line text-5xl text-white/20 mb-4 block"></i>
                <p className="text-white/40 mb-6">No strong matches found. Try a different description or fewer style filters.</p>
                <button onClick={() => goToStep(1)} className="px-6 py-3 rounded-xl text-white font-medium cursor-pointer whitespace-nowrap" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                  Start Over
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-5 mb-8">
                  {results.map(({ t, score }, idx) => {
                    const pct = normalizeScore(score, maxScore);
                    return (
                      <div key={t.name} className="group rounded-2xl overflow-hidden transition-all duration-200 hover:scale-[1.02]" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Image */}
                        <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                          <img src={t.image} alt={t.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                          {/* Match badge */}
                          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold text-white"
                            style={{ background: idx === 0 ? 'rgba(16,185,129,0.9)' : 'rgba(124,58,237,0.85)', backdropFilter: 'blur(8px)' }}>
                            {idx === 0 && <i className="ri-trophy-fill text-xs"></i>}
                            {pct}% match
                          </div>
                          {/* Category */}
                          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
                            {t.category}
                          </div>
                          {/* Hover overlay */}
                          <div className="absolute inset-0 flex items-end opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'linear-gradient(to top,rgba(0,0,0,0.7),transparent)' }}>
                            <div className="p-4 w-full flex gap-2">
                              <button onClick={() => { onPreview(t); onClose(); }} className="flex-1 py-2 rounded-xl text-xs font-bold text-white cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' }}>
                                <i className="ri-eye-line mr-1"></i>Quick Preview
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Footer */}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-sm font-bold text-white leading-tight">{t.name}</h3>
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {t.tags.slice(0, 3).map((tag) => (
                                  <span key={tag} className="px-1.5 py-0.5 text-xs rounded-md text-white/50" style={{ background: 'rgba(255,255,255,0.06)' }}>{tag}</span>
                                ))}
                              </div>
                            </div>
                            {/* Score bar */}
                            <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
                              <span className="text-xs font-bold" style={{ color: idx === 0 ? '#10b981' : '#7c3aed' }}>{pct}%</span>
                              <div className="w-16 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: idx === 0 ? '#10b981' : 'linear-gradient(90deg,#7c3aed,#06b6d4)' }}></div>
                              </div>
                            </div>
                          </div>
                          <button onClick={() => { onPreview(t); onClose(); }} className="w-full py-2 rounded-xl text-xs font-bold text-white cursor-pointer whitespace-nowrap mt-2 transition-opacity hover:opacity-90" style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' }}>
                            Use This Template
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Start over */}
                <div className="text-center pt-2 pb-4">
                  <button onClick={() => { goToStep(1); setSelectedStyles([]); setDescription(''); setResults([]); }} className="flex items-center gap-2 mx-auto px-5 py-2.5 rounded-xl text-sm text-white/50 hover:text-white transition-colors cursor-pointer whitespace-nowrap" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                    <i className="ri-refresh-line"></i>
                    Try a Different Description
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
